import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { useGetProductFamiliesQuery } from "@store/services/baseGraphFamiliesService"
import { useImmerAtom } from "jotai-immer"
import { memo } from "react"
import { ProductFamily } from "@custom-types/products"
import { AutocompleteOnChange } from "@components/NodeLink/types"
import { linkAtom } from "@components/NodeLink/linkAtom"
import { useCurrentGraphFamilyKey } from "@components/NodeLink/useCurrentGraphKey"

const FamiliesAutocomplete = memo(() => {
  const [link, updateLink] = useImmerAtom(linkAtom)

  const skip = !link.org || !link.div || !link.prod

  const { data, isFetching } = useGetProductFamiliesQuery(
    {
      roleHint: "whatif",
      organisationKey: link.org,
      divisionKey: link.div,
      productKey: link.prod,
    },
    { skip: skip },
  )

  const currentGraphFamilyKey = useCurrentGraphFamilyKey()

  // family key contains org#div#prod#family
  // we need only last bit
  const extractFamilyKey = (familyKey: string | undefined) =>
    familyKey?.split("#").reverse()[0] ?? ""

  const handleChange: AutocompleteOnChange<ProductFamily | null> = (_event, newValue) => {
    updateLink((draft) => {
      draft.family = extractFamilyKey(newValue?.key)
      draft.version = ""
      draft.node = ""
    })
  }

  const filteredFamilies: ProductFamily[] = []
  for (const family of data ?? []) {
    if (family.key === currentGraphFamilyKey) continue
    filteredFamilies.push(family)
  }

  const sortedOptions = filteredFamilies.toSorted((famA, famB) => {
    const familyKeyA = extractFamilyKey(famA.key)
    const familyKeyB = extractFamilyKey(famB.key)

    return familyKeyA.localeCompare(familyKeyB)
  })

  return (
    <Autocomplete
      options={sortedOptions}
      value={
        filteredFamilies.find((prodFamily) => extractFamilyKey(prodFamily.key) === link.family) ||
        null
      }
      getOptionLabel={(option) => option.name}
      onChange={handleChange}
      renderInput={(params) => <TextField {...params} label="Family" />}
      disabled={skip}
      loading={isFetching}
      loadingText={<CircularProgress size={20} />}
    />
  )
})
FamiliesAutocomplete.displayName = "FamiliesAutocomplete"

export default FamiliesAutocomplete
